import {ReactNode, useId} from 'react';
import './phone-number-input.scss';
import {Box, FormControl, Slide, Typography} from '@mui/material';
import CountrySelect from '../country-select/country-select';
import FlatInput from '../flat-input/flat-input';
import {Pallete} from '../../theme/bluebird';

interface PhoneNumberInputProps {
  label: string;
  placeholder?: string;
  countryCode: {code: string; phone: string};
  handleChangeCountryCode: (value: {code: string; phone: string}) => void;
  handleChangePhoneNumber: (value: string) => void;
  phoneNumber?: string;
  id?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  handleBlur?: () => void;
  countryCodeWidth?: string;
  phoneNumberWidth?: string;
  marginForSmallDevices?: string;
  disabledTooltip?: ReactNode;
}

/**
 * Phone number input field.
 */
export const PhoneNumberInput = ({
  className,
  label,
  placeholder,
  error,
  countryCode,
  handleChangeCountryCode,
  phoneNumber,
  handleChangePhoneNumber,
  handleBlur,
  disabled,
  disabledTooltip,
  required,
  ...rest
}: PhoneNumberInputProps) => {
  const hasError = !!error;

  const id = useId();

  const uniqueId = rest.id ?? `input_${id}`;

  return (
    <FormControl className={`${className} phone-number-input-container`}>
      {label && (
        <Box className="label-container">
          <Typography
            component="label"
            htmlFor={uniqueId}
            noWrap
            sx={disabled ? {color: 'var(--dcm-gray-disable) !important'} : undefined}
          >
            {label}
          </Typography>
          {required && !disabled && <Typography component="span">*</Typography>}
        </Box>
      )}

      <Box className="phone-number-input-inner-container" sx={{position: 'relative'}}>
        <Box className="phone-number-input-country-code">
          <CountrySelect
            value={countryCode}
            onChange={(value: {code: string; phone: string}) => handleChangeCountryCode(value)}
            disabled={disabled}
            required={required}
            handleBlur={handleBlur}
            error={hasError}
          />
        </Box>
        <Box
          className="phone-number-input"
          sx={{
            width: '100%',
            position: 'absolute',
          }}
        >
          <FlatInput
            id={id}
            type="tel"
            placeholder={placeholder}
            value={phoneNumber}
            onChange={(e) => handleChangePhoneNumber(e.target.value)}
            onBlur={handleBlur}
            error={hasError}
            required={required}
            disabled={disabled}
            disabledTooltip={disabledTooltip}
            InputLabelProps={{shrink: true}}
            fullWidth
            color="gray"
            sx={{
              '.MuiFilledInput-root': {
                paddingLeft: '80px',
              },
            }}
          />
        </Box>
      </Box>
      {hasError && (
        <Slide direction="left" in={hasError} mountOnEnter unmountOnExit>
          <span className="input-error" style={{color: Pallete.red.base}}>
            {error}
          </span>
        </Slide>
      )}
    </FormControl>
  );
};
